import {useCallback, useMemo} from "react";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {
    useOttopilotListCardPresentation,
    type OttopilotListCardDisplayExtras,
} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";
import {
    inferListStatusPillType,
    listStatusPillTypeToLozengeStyle,
    type ListCardStatus,
} from "~/components/ottopilotDataViewer/listStatusConfig";
import type {TableRowActionsConfig} from "~/components/table/tableRowActions.types";
import {
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
    AmaronEventTypes,
    amaronCallStatuses,
    amaronEnquiryStatuses,
    amaronServiceCategories,
    isAmaronWorkspace,
} from "~/domainModels/workspaceSpecific/amaron";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";

function resolveAmaronProfileEventStatus(event: EventWithEventContainer): ListCardStatus {
    let statusLabel = "-";

    if (event.templateId === (AmaronEventTypes.Enquiry as Uuid)) {
        const statusId = event.additionalColumnValues[AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID];
        const status = statusId ? Object.values(amaronEnquiryStatuses).find((entry) => entry.id === statusId) : undefined;
        statusLabel = status?.displayName ?? "-";
    } else if (event.templateId === (AmaronEventTypes.Calls as Uuid)) {
        const statusId = event.additionalColumnValues[AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID];
        const status = statusId ? Object.values(amaronCallStatuses).find((entry) => entry.id === statusId) : undefined;
        statusLabel = status?.displayName ?? "-";
    } else if (event.templateId === (AmaronEventTypes.Service as Uuid)) {
        const statusId = event.additionalColumnValues[AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID];
        const status = statusId ? Object.values(amaronServiceCategories).find((entry) => entry.id === statusId) : undefined;
        statusLabel = status?.displayName ?? "-";
    }

    if (statusLabel === "-") {
        return {name: statusLabel, lozengeStyle: listStatusPillTypeToLozengeStyle("Active")};
    }

    const pillType = inferListStatusPillType(statusLabel);
    return {
        name: statusLabel,
        lozengeStyle: listStatusPillTypeToLozengeStyle(pillType),
    };
}

export function useJourneyProfileEventsListPresentation({
    eventTemplates,
    workspaceCapabilities,
    tableRowActionsConfig,
    resolveCreatedByDisplayName,
}: {
    eventTemplates: Array<OttopilotEventTemplate>;
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability>;
    tableRowActionsConfig: TableRowActionsConfig<EventWithEventContainer> | undefined;
    resolveCreatedByDisplayName: (userId: Uuid | null | undefined) => string;
}) {
    const isAmaron = isAmaronWorkspace(workspaceCapabilities) === true;

    const resolveEventStatus = useCallback(
        (event: EventWithEventContainer): ListCardStatus => {
            if (isAmaron === true) {
                return resolveAmaronProfileEventStatus(event);
            }
            return {name: "Active", lozengeStyle: listStatusPillTypeToLozengeStyle("Active")};
        },
        [isAmaron],
    );

    const resolveListCardDisplayExtras = useCallback(
        (): OttopilotListCardDisplayExtras<EventWithEventContainer> => ({
            statusColumnLabel: "Status",
            tableRowActionsConfig,
        }),
        [tableRowActionsConfig],
    );

    const templateById = useMemo(
        () => new Map(eventTemplates.map((template) => [template.id, template])),
        [eventTemplates],
    );

    return useOttopilotListCardPresentation<EventWithEventContainer>(
        {
            getDisplayName: (event) =>
                templateById.get(event.templateId)?.displayName ??
                templateById.get(event.templateId)?.configuration.displayNameVariants.singularSentenceCase ??
                "Event",
            getPhoneNumber: () => null,
            getStatus: resolveEventStatus,
            getCreatedAt: (event) => event.createdAt,
            getAssignedOuName: (event) => resolveCreatedByDisplayName(event.createdBy),
            getAssignedOuColumnLabel: () => "Created by",
        },
        () => null,
        {
            // Event cards have no phone field; actions come from the row-actions config.
            onPhoneClick: () => undefined,
            resolveListCardDisplayExtras,
        },
    );
}
